import {
  EMPLOYEE_PARTS,
  clearEmployeePersistedIds,
  mergeEmployeePartResponse,
  normalizeEmployeeOptions,
  prepareEmployeeForUpsert,
} from "../utils/employeesUtils";

const hasId = (value) => value !== null && value !== undefined && value !== "";
const normalizeName = (value) => String(value ?? "").trim();
const getRecordId = (data, recordIdKey) => data?.[recordIdKey] ?? data?.id ?? null;
const createFailure = (endpoint, error, options = {}) => ({ endpoint, error, ...options });

export function createEmployeeService({ apiClient, apiUrl, interpolateURL, logError, debug }) {
  const useDebug = Boolean(debug?.useDebugMode);

  async function request({ mode, ...config }) {
    let result;
    try {
      await apiClient({
        ...config,
        mode,
        onSuccess: ({ data }) => {
          result = { ok: true, data, error: null };
        },
        onError: (error) => {
          result = { ok: false, data: null, error };
        },
      });
    } catch (error) {
      result = { ok: false, data: null, error };
    }
    return result || { ok: false, data: null, error: new Error("The API client completed without reporting a result.") };
  }
  const employeeUrl = (endpoint, id = null) => interpolateURL({ apiUrl, endpoint, id, omitLanguage: false });

  function restorePersistedIds(employee, existingEmployee) {
    if (!existingEmployee) return employee;
    return EMPLOYEE_PARTS.reduce(
      (result, { recordIdKey }) => {
        const recordId = hasId(result[recordIdKey]) ? result[recordIdKey] : existingEmployee[recordIdKey];
        return hasId(recordId) ? { ...result, [recordIdKey]: recordId } : result;
      },
      { ...employee, id: employee.id ?? existingEmployee.id }
    );
  }

  function validateUpdateIds(employee) {
    const missing = EMPLOYEE_PARTS.filter(({ recordIdKey }) => !hasId(employee[recordIdKey])).map(({ recordIdKey }) => recordIdKey);
    if (missing.length) throw new Error(`Cannot PATCH employee because these record IDs are missing: ${missing.join(", ")}`);
  }

  function addPersistenceErrors(employee, error) {
    const errors = { ...(employee?.errors || {}) };
    const responseData = error?.response?.data;
    if (Array.isArray(responseData?.errors)) {
      responseData.errors.forEach(({ field, title }) => {
        if (field) errors[field] = title;
      });
    }
    if (!Object.keys(errors).length) errors.nameOrNum = responseData?.message || error?.message || "Employee persistence failed.";
    return { ...employee, errors };
  }

  function requireReload(employee, message) {
    const previousMessage = employee?.errors?.nameOrNum;
    return {
      ...employee,
      persistenceUncertain: true,
      errors: { ...(employee?.errors || {}), nameOrNum: previousMessage ? `${previousMessage} ${message}` : message },
    };
  }

  async function persistPart({ employee, part, operation, companyId, companyInputId, index, hash }) {
    const method = operation === "update" ? "PATCH" : "POST";
    const recordId = operation === "update" ? employee[part.recordIdKey] : null;
    if (useDebug) console.log(`====== ${method} /${part.endpoint} =====`);
    const result = await request({
      mode: "sequence",
      method,
      url: employeeUrl(part.endpoint, recordId),
      body: part.toDTO(employee, { companyId, companyInputId }),
      hash,
      ignoreError: true,
      isFirstStep: index === 0,
      isLastStep: index === EMPLOYEE_PARTS.length - 1,
    });
    if (!result.ok)
      return { ok: false, failure: createFailure(part.endpoint, result.error || new Error(`/${part.endpoint} did not complete successfully.`)) };
    if (result.data == null) {
      if (operation === "update") return { ok: true, employee };

      return {
        ok: false,
        failure: createFailure(part.endpoint, new Error(`POST /${part.endpoint} succeeded without returning the persisted record.`), {
          requiresReload: part.endpoint === "employee",
        }),
      };
    }
    if (useDebug) console.log(`/${part.endpoint}:`, result.data);
    const persistedRecordId = getRecordId(result.data, part.recordIdKey);
    try {
      let mergedEmployee = mergeEmployeePartResponse(employee, result.data, part);
      if (operation === "create" && part.endpoint === "employee" && hasId(mergedEmployee.employeeId))
        mergedEmployee = { ...mergedEmployee, id: String(mergedEmployee.employeeId) };
      if (operation === "create" && !hasId(mergedEmployee[part.recordIdKey])) {
        return {
          ok: false,
          failure: createFailure(part.endpoint, new Error(`POST /${part.endpoint} did not return ${part.recordIdKey}.`), {
            persistedRecordId,
            requiresReload: part.endpoint === "employee" && !hasId(persistedRecordId),
          }),
        };
      }
      return { ok: true, employee: mergedEmployee };
    } catch (error) {
      return {
        ok: false,
        failure: createFailure(part.endpoint, error, {
          persistedRecordId,
          requiresReload: part.endpoint === "employee" && !hasId(persistedRecordId),
        }),
      };
    }
  }

  async function finishFailure({ employee, operation, isUpdate, failure, hash }) {
    const endpoint = failure?.endpoint || "employee";
    const error = failure?.error || new Error("Employee persistence failed.");
    console.error(`/${endpoint} Error =====`, error?.response?.data?.errors || error?.response?.data || error);
    let failedEmployee = employee;
    if (operation === "create" && endpoint === "employee" && hasId(failure?.persistedRecordId) && !hasId(failedEmployee.employeeId))
      failedEmployee = { ...failedEmployee, employeeId: failure.persistedRecordId, id: String(failure.persistedRecordId) };
    failedEmployee = addPersistenceErrors(failedEmployee, error);
    let requiresReload = Boolean(operation === "update" || failure?.requiresReload || error?.requiresReload);
    if (requiresReload) failedEmployee = requireReload(failedEmployee, "Reload the survey before retrying.");
    const rollbackId = failedEmployee.employeeId ?? (endpoint === "employee" ? failure?.persistedRecordId : null);
    if (operation === "create" && !requiresReload && hasId(rollbackId)) {
      const rollback = await deleteEmployee({ id: rollbackId, hash });
      if (rollback.ok) failedEmployee = clearEmployeePersistedIds(failedEmployee);
      else {
        console.error("Employee creation rollback failed:", rollback.error);
        failedEmployee = requireReload(failedEmployee, "The partially created employee could not be removed. Reload the survey before retrying.");
        requiresReload = true;
      }
    }
    return { ok: false, employee: failedEmployee, isUpdate, error, requiresReload };
  }

  async function upsertEmployee({
    employee: employeeInput,
    companyId,
    companyInputId,
    dropdownOptions,
    employees: existingEmployees = [],
    hash,
    engine,
  }) {
    let employee = { ...(employeeInput || {}), errors: {} };
    let operation = "create";
    let isUpdate = false;
    try {
      const prepared = await prepareEmployeeForUpsert({ employeeInput, existingEmployees, engine });
      employee = { ...prepared.employee, errors: {} };
      isUpdate = prepared.canUpdate;
      operation = isUpdate ? "update" : "create";
      if (isUpdate) {
        employee = restorePersistedIds(employee, prepared.existingEmployee);
        if (prepared.existingEmployee) {
          const nameChanged = normalizeName(employee.nameOrNum) !== normalizeName(prepared.existingEmployee.nameOrNum);
          employee.hash = nameChanged
            ? await engine.encryptEmployee(normalizeName(employee.nameOrNum))
            : prepared.existingEmployee.hash || employee.hash;
        } else employee.hash = await engine.encryptEmployee(normalizeName(employee.nameOrNum));
        validateUpdateIds(employee);
      }
      employee.companyHash = hash;
      for (let index = 0; index < EMPLOYEE_PARTS.length; index += 1) {
        const result = await persistPart({ employee, part: EMPLOYEE_PARTS[index], operation, companyId, companyInputId, index, hash });
        if (!result.ok) return finishFailure({ employee, operation, isUpdate, failure: result.failure, hash });
        employee = result.employee;
      }
      return {
        ok: true,
        employee: { ...normalizeEmployeeOptions(employee, dropdownOptions), errors: {} },
        isUpdate,
        error: null,
        requiresReload: false,
      };
    } catch (error) {
      return finishFailure({
        employee,
        operation,
        isUpdate,
        failure: createFailure("employee", error, { requiresReload: Boolean(error?.requiresReload) }),
        hash,
      });
    }
  }

  async function deleteEmployee({ id, hash }) {
    if (useDebug) debug.enhancedLog(useDebug, "Delete Employee", "red");
    const result = await request({ mode: "resolution", method: "DELETE", url: employeeUrl("employee", id), hash, ignoreError: !useDebug });
    if (!result.ok) {
      logError("employee", result.error);
      return { ok: false, data: false, error: result.error };
    }
    if (useDebug) console.log("Deleted Employee", id);
    return { ok: true, data: true, error: null };
  }

  async function deleteEmployeeInBulk({ ids = [], hash }) {
    if (useDebug) debug.enhancedLog(useDebug, "Delete Employee in bulk", "red");
    const failures = [];
    const deletedIds = [];
    for (let index = 0; index < ids.length; index += 1) {
      const id = ids[index];
      const result = await request({
        mode: "sequence",
        method: "DELETE",
        url: employeeUrl("employee", id),
        hash,
        ignoreError: !useDebug,
        isFirstStep: index === 0,
        isLastStep: index === ids.length - 1,
      });
      if (!result.ok) {
        failures.push({ id, error: result.error });
        logError("employee", result.error);
      } else {
        deletedIds.push(id);
        if (useDebug) console.log("Deleted Employee", id);
      }
    }
    return { ok: failures.length === 0, data: failures.length === 0, error: failures.length ? failures : null, failures, deletedIds };
  }

  async function listEmployees(options = {}) {
    const hash = options?.hash;
    const result = await request({ mode: "resolution", method: "GET", url: employeeUrl("employee"), hash, ignoreError: false });
    if (!result.ok) {
      logError("employee", result.error);
      return { ok: false, data: [], error: result.error };
    }
    return { ok: true, data: result.data || [], error: null };
  }

  return { deleteEmployee, deleteEmployeeInBulk, upsertEmployee, listEmployees };
}
